import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { motion } from "framer-motion";
import { Heart, Baby, Sparkles, Calendar, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Chip = ({ children }) => (
  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-white/90 border border-white/10">
    {children}
  </span>
);

const PregnancyBlock = ({ data, partnerName }) => {
  const { t } = useTranslation();
  const content = data.content || {};
  return (
    <div className="space-y-6">
      <div className="text-center">
        <p className="text-sm uppercase tracking-widest text-pink-300/80 mb-2">
          {t("partner.weekTrimester", { week: data.week, trimester: data.trimester })}
        </p>
        <h2 className="text-4xl sm:text-5xl font-serif text-white">
          {t("partner.babySize", { size: content.size || t("partner.babyTiny") })}
        </h2>
        {content.size_emoji && (
          <div className="text-6xl mt-4" aria-hidden>{content.size_emoji}</div>
        )}
      </div>

      {content.highlight && (
        <div className="rounded-2xl bg-gradient-to-br from-pink-500/15 to-purple-500/10 border border-pink-400/20 p-6">
          <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-pink-300" /> {t("partner.thisWeek")}
          </h3>
          <p className="text-white/85 leading-relaxed">{content.highlight}</p>
        </div>
      )}

      {content.tip && (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
            <Heart className="w-4 h-4 text-rose-300" /> {t("partner.howToSupport", { name: partnerName })}
          </h3>
          <p className="text-white/85 leading-relaxed">{content.tip}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2 justify-center">
        {data.due_date && <Chip>{t("partner.dueOn", { date: new Date(data.due_date).toLocaleDateString() })}</Chip>}
        {typeof data.days_to_due === "number" && data.days_to_due > 0 && (
          <Chip>{t("partner.daysToGo", { days: data.days_to_due })}</Chip>
        )}
        {typeof data.kicks_last_24h === "number" && data.kicks_last_24h > 0 && (
          <Chip>{t("partner.kicks24h", { count: data.kicks_last_24h })}</Chip>
        )}
      </div>
    </div>
  );
};

const TTCBlock = ({ data, partnerName }) => {
  const { t } = useTranslation();
  if (data.message) {
    return (
      <div className="rounded-2xl bg-white/5 border border-white/10 p-8 text-center">
        <Calendar className="w-10 h-10 text-pink-300 mx-auto mb-3" />
        <p className="text-white/80">{t("partner.noCycleData")}</p>
      </div>
    );
  }

  const in_window = data.in_fertile_window;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
  const ovulationText =
    data.days_until_ovulation === 1
      ? t("partner.ovulationIn", { days: 1 })
      : t("partner.ovulationIn_plural", { days: data.days_until_ovulation });

  return (
    <div className="space-y-6">
      <div className={`rounded-2xl p-6 text-center border ${in_window ? "bg-gradient-to-br from-rose-500/25 to-pink-500/10 border-rose-400/40" : "bg-white/5 border-white/10"}`}>
        <p className="text-sm uppercase tracking-widest text-pink-300/80 mb-2">{t("partner.fertileStatus")}</p>
        <h2 className="text-3xl sm:text-4xl font-serif text-white">
          {in_window ? t("partner.inWindow") : t("partner.notInWindow")}
        </h2>
        <p className="text-white/80 mt-3">
          {in_window
            ? t("partner.tonightMessage", { name: partnerName })
            : data.days_until_ovulation >= 0
              ? ovulationText
              : t("partner.waitNextCycle")}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-center">
          <p className="text-xs text-white/60 uppercase tracking-wider">{t("partner.fertileWindow")}</p>
          <p className="text-white font-medium mt-1">
            {fmt(data.fertile_window_start)} – {fmt(data.fertile_window_end)}
          </p>
        </div>
        <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-center">
          <p className="text-xs text-white/60 uppercase tracking-wider">{t("partner.ovulation")}</p>
          <p className="text-white font-medium mt-1">{fmt(data.ovulation_date)}</p>
        </div>
        <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-center">
          <p className="text-xs text-white/60 uppercase tracking-wider">{t("partner.cycleLength")}</p>
          <p className="text-white font-medium mt-1">{t("partner.cycleDays", { count: data.cycle_length })}</p>
        </div>
      </div>
    </div>
  );
};

export default function PartnerView() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const lang = (i18n.language || "en").split("-")[0];
        const res = await axios.get(`${API}/partner/view/${token}`, { params: { lang } });
        if (!cancelled) setState({ loading: false, data: res.data, error: null });
      } catch (err) {
        const detail = err?.response?.data?.detail || t("partner.linkUnavailable");
        if (!cancelled) setState({ loading: false, data: null, error: detail });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token, t, i18n.language]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#0d0821] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-pink-300 animate-spin" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-[#0d0821] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-white mb-2">{t("partner.linkUnavailable")}</h1>
          <p className="text-white/70" data-testid="partner-view-error">{state.error}</p>
        </div>
      </div>
    );
  }

  const { partner_name, mode, pregnancy, ttc } = state.data || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0d0821] via-[#1a0f2e] to-[#0d0821] text-white">
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
        <motion.header
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-10"
          data-testid="partner-view-header"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/15 border border-pink-400/20 text-pink-200 text-xs uppercase tracking-widest mb-4">
            {mode === "pregnancy" ? <Baby className="w-3.5 h-3.5" /> : <Heart className="w-3.5 h-3.5" />}
            {t("partner.viewHeader")}
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif">{t("partner.journeyHeader", { name: partner_name })}</h1>
          <p className="text-white/60 text-sm mt-2">{t("partner.readOnly")}</p>
        </motion.header>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {mode === "pregnancy" && pregnancy && (
            <PregnancyBlock data={pregnancy} partnerName={partner_name} />
          )}
          {mode === "ttc" && ttc && <TTCBlock data={ttc} partnerName={partner_name} />}
          {mode === "standard" && (
            <div className="rounded-2xl bg-white/5 border border-white/10 p-8 text-center">
              <p className="text-white/80">{t("partner.standardMessage")}</p>
            </div>
          )}
        </motion.div>

        <footer className="mt-14 text-center text-white/40 text-xs">
          {t("partner.poweredBy", { name: partner_name })}
        </footer>
      </div>
    </div>
  );
}
